'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { loadJs, loadCss } from '@nx-frontend/features/web-editor/util';
import { $ModalStore, closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal/modal-store';
import { SkeletonType, SkeletonWrapper } from '@nx-frontend/shared/ui/skeletons';
import { goToSettings } from '@nx-frontend/shared/actions';
import { generalEnv } from '@nx-frontend/shared/env';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $workspaceAccess } from '@nx-frontend/shared/store/workspace-access';
import {
  PopupType,
  InvitePopupPayload,
  OrgMemberRole,
  OrgInfoSubscriptionType,
  OrganizationPrivilege,
  EditorColorScheme,
} from '@nx-frontend/shared/types';
import { WorkspacePrivilege } from '@nx-frontend/shared/types';
import { GetInvivitePopupProps, InviteFeatureSubject } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { NextAppRouterPushFunction, NextRouterPushFunction, NextRouterType } from '@nx-frontend/shared/utils';
import { getAdminClientPath } from '@nx-frontend/shared/utils';

export const getOpenInvitePopup =
  (
    store: GetInvivitePopupProps,
    openModal: (params: OpenModalParams) => void,
    router?: {
      routerType: NextRouterType;
      routerPushFn: NextRouterPushFunction | NextAppRouterPushFunction;
    }
  ) =>
  (payload: InvitePopupPayload) => {
    const { orgInfo, currentWorkspace, role } = store;
    const { orgId } = orgInfo;
    const { globalId: wsGlobalId } = currentWorkspace;
    const { role: orgRole } = role;

    if (router) {
      const { routerType, routerPushFn } = router;

      if (['standard'].includes(store.orgInfo.subscriptionType) && orgRole === OrgMemberRole.OWNER) {
        goToSettings({ routerType, routerPushFn }, getAdminClientPath(orgId, wsGlobalId));
        return;
      }

      if (!payload.skipInviteRedirect) {
        if (orgRole === OrgMemberRole.OWNER || orgRole === OrgMemberRole.MANAGER) {
          goToSettings({ routerType, routerPushFn }, `/org/${orgId}/members/invite?workspace=${wsGlobalId}`);
          return;
        }
      }
    }

    openModal({
      content: PopupType.INVITE,
      showCloseButton: false,
      payload,
      multipopup: false,
    });
  };

export const isIvitePopupBundleLoaded = () => {
  return document.createElement('nim-invite').constructor !== HTMLElement;
};

let apiInited = false;

export function InvitePopup() {
  const [app, currentWorkspace, billingInfo, orgInfo, role, workspaceAccess] = useUnit([
    $app,
    $currentWorkspace,
    $billingInfo,
    $orgInfo,
    $orgRoleState,
    $workspaceAccess,
  ]);

  const [store, closeModal, openModal] = useUnit([$ModalStore, closeModalEvent, openModalEvent]);

  const nimInviteRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(isIvitePopupBundleLoaded());
  const onInvitePopupLoaded = useCallback(() => {
    let featureSubject: InviteFeatureSubject | null = null;

    const modal = store.find((modal) => modal.payload && modal.payload.payloadType === PopupType.INVITE);
    if (modal) {
      const payload = modal.payload as InvitePopupPayload;

      if (payload.subjectType === 'workspace') {
        featureSubject = {
          globalId: currentWorkspace.globalId,
          title: currentWorkspace.title,
          subjectType: payload.subjectType,
        };
      }

      if (payload.subjectType === 'note' || payload.subjectType === 'folder') {
        featureSubject = payload;
      }
    }

    if (!apiInited) {
      const setupApiEvent = new CustomEvent('ng2:management:setup', {
        detail: {
          gatewayUrl: generalEnv.gatewayUrl,
          gatewayToken: app.sessionId,
          staticAssetsPrefix: generalEnv.staticAssetPrefix,
        },
      });
      document.dispatchEvent(setupApiEvent);

      const loadAllEvent = new CustomEvent('ng2:management:load-all', {
        detail: {
          organizationId: currentWorkspace.orgId,
        },
      });
      document.dispatchEvent(loadAllEvent);
      apiInited = true;
    }

    if (document.querySelector('nim-invite')) {
      setIsLoaded(true);
      return;
    }

    let featGuestsEnabled = ['business'].includes(orgInfo.subscriptionType);
    if (featureSubject) {
      const { subjectType } = featureSubject;
      if (['note', 'folder'].includes(subjectType)) {
        featGuestsEnabled = ![OrgInfoSubscriptionType.FREE_ACCOUNT, OrgInfoSubscriptionType.STANDARD].includes(
          orgInfo.subscriptionType
        );
      }
    }

    // if user does not have privilege to add members to the org then only member, guest and client roles is allowed to be added
    // considering that the user is admin of the workspace
    const featAllowedRoles = role.privileges?.includes(OrganizationPrivilege.CAN_ADD_MEMBERS)
      ? null
      : ['member', 'guest', 'client'];

    const nimInvite = document.createElement('nim-invite');
    Object.assign(nimInvite, {
      featureContext: {
        organizationId: currentWorkspace.orgId,
        workspaceId: currentWorkspace.globalId,
      },
      featureSubject,
      featGuestsEnabled,
      featEmailInvitesEnabled: workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_MANAGE_MEMBERS),
      isBusinessTrialActive: billingInfo.plan === 'trial' && billingInfo.active,
      isCreditForInviteEnabled: true,
      featAllowedRoles,
    });

    nimInvite.addEventListener('nimClose', () => {
      closeModal();
      if (featureSubject && featureSubject.backToManage) {
        openModal({
          content: PopupType.MANAGE,
          showCloseButton: false,
          payload: featureSubject.backToManage,
        });
      }
    });
    nimInvite.addEventListener('nimContact', () => {
      window.open(
        generalEnv.bookDemoUrl + '?utm_source=invites_limits&utm_medium=invites_limits&utm_campaign=invites_limits',
        '_blank'
      );
    });

    setIsLoaded(true);
    nimInviteRef.current?.appendChild(nimInvite);
  }, [
    app.sessionId,
    billingInfo.active,
    billingInfo.plan,
    currentWorkspace.globalId,
    currentWorkspace.orgId,
    currentWorkspace.title,
    orgInfo.subscriptionType,
    store,
    role.privileges,
    closeModal,
    openModal,
    workspaceAccess.privileges,
  ]);

  useEffect(() => {
    loadCss({ url: generalEnv.tasksBundleCssUrl, id: 'tasks-bundle-css' });
    loadJs({ url: generalEnv.invitesBundleJsUrl, id: 'invite-popup-bundle' });

    let IID: NodeJS.Timer | number | null = null;
    if (!isIvitePopupBundleLoaded()) {
      IID = setInterval(() => {
        if (isIvitePopupBundleLoaded()) {
          onInvitePopupLoaded();
          IID && clearInterval(IID);
        }
      }, 500);
    } else {
      onInvitePopupLoaded();
    }

    return () => {
      IID && clearInterval(IID);
      document.querySelector('nim-invite')?.remove();
    };
  }, [onInvitePopupLoaded]);

  return (
    <div data-cy="invite-popup" className="w-[602px] min-h-[200px] relative nimbus-popup-content" ref={nimInviteRef}>
      {!isLoaded && <SkeletonWrapper type={SkeletonType.INVITE_POPUP} editorColorScheme={EditorColorScheme.LIGHT} />}
    </div>
  );
}
